import { PublicCallback } from 'globals/types';
import { handleError } from 'globals/errors';
import {
  fetchMessageAttachments,
  fetchHistoricalMessages,
} from 'core_main/api/messages';
import { GetHistoricalChatMessagesApiResponse } from 'core_main/api/messages/types';
import { DEFAULT_HISTORICAL_MESSAGE_PAGE_SIZE } from 'core/constants';
import { createGetHistoricalMessagesCallbackResponse } from './helpers';
import {
  GetHistoricalMessagesParameters,
  HistoricalMessagesWithAttachments,
} from './types';

const addAttachmentsToHistoricalMessages = (
  history: GetHistoricalChatMessagesApiResponse,
) => {
  const messagesWithAttachments = history?.data?.map((val) => {
    return new Promise<HistoricalMessagesWithAttachments>((resolve) => {
      const attachments = val?.relationships?.attachments;

      if (attachments) {
        fetchMessageAttachments(val.id).then((attachmentsResponses) => {
          resolve({ message: val, attachments: attachmentsResponses });
        });
        return;
      }

      resolve({ message: val });
    });
  });

  return Promise.allSettled(messagesWithAttachments).then((res) => {
    const messageList: HistoricalMessagesWithAttachments[] = [];
    res.forEach((message) => {
      if (message.status !== 'rejected') {
        messageList.push(message.value);
      } else {
        handleError(message.reason, {
          message: 'Error appending attachment(s) to historical message',
        });
      }
    });
    return {
      messages: messageList,
      links: history?.links,
    };
  });
};

// We should consider deprecating this sort of historical fetch from the SDK
// There should be one source of truth for historical messages exposed, and whether it uses pubnub or kustomer API is an implementation detail
export const getHistoricalMessages = (
  parameters: GetHistoricalMessagesParameters,
  cb?: PublicCallback,
) => {
  const conversationId = parameters?.conversationId;
  const count = parameters?.count || DEFAULT_HISTORICAL_MESSAGE_PAGE_SIZE;
  const before = parameters?.before;

  return fetchHistoricalMessages(conversationId, {
    count,
    before,
  })
    .then(addAttachmentsToHistoricalMessages)
    .then((history) => {
      const callbackResponse = createGetHistoricalMessagesCallbackResponse(
        history?.messages,
        conversationId,
        history.links,
      );

      if (typeof cb === 'function') {
        cb(callbackResponse);
      }
    })
    .catch((error) => {
      if (typeof cb === 'function') cb(null, error);
      handleError(error, { message: 'Failed to get historical messages' });
    });
};
